/**
 * BUNDLE SELECTOR
 *
 * Lets the customer buy the product of the page together with components picked from collections (extra lenses,
 * straps, accessories). Bundles are not products: every part is added to the cart as its own line item, exactly like
 * the catalogue is built. The saving is granted by a discount code that is applied right after the items are added.
 *
 * The element mirrors what Impact's own <product-form> does after a successful add (variant:add + cart:change), so the
 * cart popover and the cart count react the same way they do for a normal add to cart.
 */
(() => {
  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '{{ amount }}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!match) return String(cents / 100);

    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number === null) return '0';

      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

      return parts[1] ? units + decimal + parts[1] : units;
    };

    let value = '';

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(match[0], value);
  };

  class BundleSelector extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.getAttribute('form'));
      this.basePrice = Number(this.getAttribute('base-price') || 0);
      this.baseVariantId = this.getAttribute('base-variant-id');
      this.options = Array.from(this.querySelectorAll('[data-bundle-option]'));

      try {
        this.variants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '{}');
      } catch (error) {
        this.variants = {};
      }

      this._onChoiceChange = this.onChoiceChange.bind(this);
      this._onComponentChange = this.onComponentChange.bind(this);
      this._onVariantChange = this.onVariantChange.bind(this);
      this._onSubmit = this.onSubmit.bind(this);

      this.addEventListener('change', this._onChoiceChange);
      this.addEventListener('change', this._onComponentChange);
      document.documentElement.addEventListener('variant:change', this._onVariantChange);

      // A swatch app may replace the theme's variant picker and never fire variant:change, so the selected variant is
      // always re-read from the form itself, which stays authoritative either way.
      this.form?.addEventListener('change', this._onVariantChange);

      // Capture on an ancestor so this runs before the listener <product-form> attached to the form itself.
      document.documentElement.addEventListener('submit', this._onSubmit, { capture: true });

      this.refresh();
      this.ready = true;
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('variant:change', this._onVariantChange);
      document.documentElement.removeEventListener('submit', this._onSubmit, { capture: true });
      this.form?.removeEventListener('change', this._onVariantChange);
    }

    get selectedOption() {
      const radio = this.querySelector('[data-bundle-radio]:checked');
      return radio ? radio.closest('[data-bundle-option]') : null;
    }

    onChoiceChange(event) {
      if (!event.target.matches('[data-bundle-radio]')) return;
      this.refresh();
    }

    onComponentChange(event) {
      const select = event.target;
      if (!select.matches('[data-bundle-component-select]')) return;

      const component = select.closest('[data-bundle-component]');
      const option = select.closest('[data-bundle-option]');
      const chosen = select.options[select.selectedIndex];
      const image = component?.querySelector('.bundle-component__image');
      const thumb = option?.querySelector(`[data-bundle-thumb="${component?.dataset.componentIndex}"] img`);

      [image, thumb].forEach((element) => {
        if (!element || !chosen.dataset.image) return;

        element.removeAttribute('srcset');
        element.src = chosen.dataset.image;
        element.alt = chosen.dataset.title || '';
      });

      this.refresh();
    }

    onVariantChange(event) {
      const variant = event?.detail?.variant;

      if (variant) {
        this.applyBase(String(variant.id), variant.price, variant.available, variant.featured_media?.preview_image?.src);
        return;
      }

      this.syncBaseFromForm();
    }

    /**
     * Reads the variant the customer actually has selected straight from the product form.
     */
    syncBaseFromForm() {
      const input = this.form?.querySelector('[name="id"]');
      const id = input?.value;

      if (!id || id === this.baseVariantId) return;

      const variant = this.variants[id];
      this.applyBase(id, variant ? variant.price : this.basePrice, variant ? variant.available : true, variant?.image);
    }

    applyBase(id, price, available, image) {
      this.baseVariantId = id;
      this.basePrice = Number(price) || this.basePrice;

      this.options.forEach((option) => {
        const thumb = option.querySelector('[data-bundle-thumb-base] img');

        if (thumb && image) {
          thumb.removeAttribute('srcset');
          thumb.src = image;
        }

        if (!option.dataset.bundleId) return;

        const radio = option.querySelector('[data-bundle-radio]');

        option.dataset.available = String(Boolean(available));
        radio.disabled = !available;

        if (!available && radio.checked) {
          const fallback = this.querySelector('[data-bundle-single] [data-bundle-radio]');
          if (fallback) fallback.checked = true;
        }
      });

      this.refresh();
    }

    refresh() {
      this.options.forEach((option) => {
        const selected = Boolean(option.querySelector('[data-bundle-radio]:checked'));
        option.classList.toggle('is-selected', selected);

        // A folded picker must not be reachable with the keyboard
        option.querySelector('.bundle-option__config')?.toggleAttribute('inert', !selected);

        if (!option.dataset.bundleId) return;

        const discount = this.discountFor(option);
        const total = this.totalFor(option);

        const priceElement = option.querySelector('[data-bundle-price]');
        const compareElement = option.querySelector('[data-bundle-compare]');
        const savingElement = option.querySelector('[data-bundle-saving-amount]');

        if (priceElement) this.setPrice(priceElement, formatMoney(Math.max(total - discount, 0)));
        if (compareElement) compareElement.textContent = formatMoney(total);
        if (savingElement) savingElement.textContent = formatMoney(discount);
      });

      this.updateButtonLabel();
    }

    setPrice(element, text) {
      if (element.textContent === text) return;

      element.textContent = text;
      if (!this.ready) return;

      // Restart the animation so every change is visible, not just the first one
      element.classList.remove('is-changing');
      void element.offsetWidth;
      element.classList.add('is-changing');
    }

    /**
     * Shows the bundle price on the add to cart button, so the customer sees what they commit to before clicking.
     * A disabled button (sold out, or an add in progress) is left to the theme.
     */
    updateButtonLabel() {
      const button = this.form?.querySelector('button[type="submit"]');
      const label = button?.querySelector('div');

      if (!label || button.disabled) return;

      this.defaultLabel = this.defaultLabel || window.themeVariables?.strings?.addToCartButton || label.textContent.trim();

      const option = this.selectedOption;
      const price = option?.dataset.bundleId ? option.querySelector('[data-bundle-price]')?.textContent.trim() : '';

      label.textContent = price ? `${this.defaultLabel} · ${price}` : this.defaultLabel;
    }

    /**
     * The discount is configured in the store currency, while prices on the page are in the customer's currency.
     * Shopify converts fixed-amount discounts with the same rate, so the displayed saving matches the checkout.
     */
    discountFor(option) {
      const rate = Number(window.Shopify?.currency?.rate || 1) || 1;
      return Math.round(Number(option.dataset.discount || 0) * rate);
    }

    totalFor(option) {
      return this.componentsOf(option).reduce((total, item) => total + item.price, this.basePrice);
    }

    componentsOf(option) {
      return Array.from(option.querySelectorAll('[data-bundle-component-select]')).map((field) => {
        // A component is either a choice (select) or a fixed product (hidden input carrying the same data)
        const chosen = field.tagName === 'SELECT' ? field.options[field.selectedIndex] : field;

        return {
          id: chosen.value,
          price: Number(chosen.dataset.price || 0),
          title: chosen.dataset.title || ''
        };
      });
    }

    onSubmit(event) {
      if (event.target !== this.form) return;

      const option = this.selectedOption;
      if (!option || !option.dataset.bundleId) return;

      event.preventDefault();
      event.stopPropagation();

      this.syncBaseFromForm();
      this.addBundleToCart(option);
    }

    async addBundleToCart(option) {
      const buttons = Array.from(this.form.elements).filter((element) => element.type === 'submit');

      buttons.forEach((button) => {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      });

      const properties = {
        _bundle_id: option.dataset.bundleId,
        _bundle_title: option.dataset.bundleTitle || ''
      };

      const items = [
        { id: Number(this.baseVariantId), quantity: 1, properties },
        ...this.componentsOf(option).map((component) => ({
          id: Number(component.id),
          quantity: 1,
          properties
        }))
      ];

      try {
        const sections = ['variant-added'];

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: `${Shopify.routes.root}variants/${this.baseVariantId}`
          })
        });

        const payload = await response.json();

        if (!response.ok) {
          this.form.dispatchEvent(
            new CustomEvent('cart:error', { bubbles: true, detail: { error: payload.description } })
          );
          return;
        }

        await this.applyDiscount(option.dataset.discountCode);

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = payload.sections;

        this.form.dispatchEvent(
          new CustomEvent('variant:add', { bubbles: true, detail: { items: payload.items || [payload], cart } })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
        );
      } catch (error) {
        this.form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: String(error) } }));
      } finally {
        buttons.forEach((button) => {
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
        });

        this.updateButtonLabel();
      }
    }

    /**
     * The saving itself lives in Shopify, not here: /discount/<code> stores the code on the cart, so the reduction is
     * calculated server-side and survives into checkout. Without a code the bundle is simply a convenient add to cart.
     */
    async applyDiscount(code) {
      if (!code) return;

      try {
        // Redirecting to cart.js keeps the response tiny instead of downloading the home page.
        await fetch(`${Shopify.routes.root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
      } catch (error) {
        console.error('[bundle-selector] could not apply the discount code', error);
      }
    }
  }

  if (!window.customElements.get('bundle-selector')) {
    window.customElements.define('bundle-selector', BundleSelector);
  }
})();
